import { useEffect, useState } from 'react';
import './../styles/cart.css';
import { HashLoader } from 'react-spinners';
import ToastNotification, { showToast } from '../components/toasts/ToastNotification';
import { deleteFromCart, getCart, removeCartItem } from '../api/cart.api';
import { MdDelete } from "react-icons/md";
import EMPTY_CART from './../assets/images/empty_cart.png';
import { useUser } from '../context/UserContext';
import { useNavigate } from 'react-router-dom';
import { FaArrowRightLong } from "react-icons/fa6";

export const Cart = () => {
  const [loading, setLoading] = useState(false);
  const [cart, setUserCart] = useState(null);
  const [selectedItemKeys, setSelectedItemKeys] = useState([]);
  const { setCart } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Giỏ hàng của bạn";
    fetchCart();
  }, []);

  const fetchCart = async () => {
    try {
      setLoading(true);

      const res = await getCart();

      if (res.success) {
        const nextCart = res.data.cart;
        const nextProducts = Array.isArray(nextCart?.products) ? nextCart.products : [];
        const nextKeys = nextProducts.map((item) => `${item?.productId}_${item?.packageId || "default"}`);
        const allowedKeys = nextKeys.slice(0, 4);

        setUserCart(nextCart);
        setSelectedItemKeys((prev) => {
          const validPrev = prev.filter((key) => allowedKeys.includes(key));
          return validPrev.length > 0 ? validPrev : allowedKeys;
        });
        setCart(Number(res?.data?.cart?.totalItems || 0));
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCart = async () => {
    try {
      setLoading(true);

      const res = await deleteFromCart();

      if (res.success) {
        setUserCart(null);
        setSelectedItemKeys([]);
        setCart(0);
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const getProductPriceLabel = (product) => {
    if (!product) return "0đ";
    if (product.product_type === "topup_package") {
      const selectedPrice = Number(product?.selected_package?.price || 0);
      return `${selectedPrice.toLocaleString("vi-VN")}đ`;
    }

    const directPrice = Number(product?.product_attributes?.price || 0);
    return `${directPrice.toLocaleString("vi-VN")}đ`;
  };

  const getNumericPrice = (product) => {
    if (!product) return 0;

    if (product.product_type === "topup_package") {
      return Number(product?.selected_package?.price || 0);
    }

    return Number(product?.product_attributes?.price || 0);
  };

  const handleRemoveItem = async (productId, packageId = null) => {
    try {
      setLoading(true);
      const res = await removeCartItem(productId, packageId);
      if (res?.success) {
        await fetchCart();
        setCart(Number(res?.data?.cartCount || 0));
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const getCartItemKey = (item) => `${item?.productId}_${item?.packageId || "default"}`;

  const handleToggleSelectItem = (item) => {
    const key = getCartItemKey(item);
    setSelectedItemKeys((prev) => {
      if (prev.includes(key)) {
        return prev.filter((itemKey) => itemKey !== key);
      }

      if (prev.length >= 4) {
        showToast("Chỉ được chọn tối đa 4 sản phẩm", "warning");
        return prev;
      }

      return [...prev, key];
    });
  };

  const handleToggleSelectAll = (products) => {
    const keys = products.map((item) => getCartItemKey(item)).slice(0, 4);
    const isAllSelected = keys.length > 0 && keys.every((key) => selectedItemKeys.includes(key));

    if (!isAllSelected && products.length > 4) {
      showToast("Chỉ chọn nhanh tối đa 4 sản phẩm để thanh toán", "warning");
    }

    setSelectedItemKeys(isAllSelected ? [] : keys);
  };

  if (loading) {
    return (
      <div style={{ height: "100vh" }} className="loader-container">
        <HashLoader color="var(--hash-loader-color)" />
      </div>
    );
  }

  const activeProducts = Array.isArray(cart?.products) ? cart.products : [];
  const selectableKeys = activeProducts.map((item) => getCartItemKey(item)).slice(0, 4);
  const selectedProducts = activeProducts.filter((item) =>
    selectedItemKeys.includes(getCartItemKey(item))
  );
  const subtotal = selectedProducts.reduce((sum, product) => sum + getNumericPrice(product), 0);
  const isAllSelected = selectableKeys.length > 0 &&
    selectableKeys.every((key) => selectedItemKeys.includes(key));

  return (
    <section className="cartx-shell">
      <ToastNotification />
      <div className="cartx-container">
        <header className="cartx-hero">
          <div>
            <h1 className="cartx-title">Giỏ hàng của bạn</h1>
            <p className="cartx-subtitle">
              {activeProducts.length > 0
                ? "Chọn sản phẩm muốn thanh toán (tối đa 4 sản phẩm)."
                : "Hiện tại chưa có sản phẩm nào trong giỏ."}
            </p>
          </div>
          {activeProducts.length > 0 && (
            <button
              onClick={handleDeleteCart}
              className="cartx-clear-btn"
              type="button"
              aria-label="Xóa sản phẩm khỏi giỏ hàng"
            >
              <MdDelete />
              <span>Làm trống giỏ</span>
            </button>
          )}
        </header>

        {activeProducts.length > 0 ? (
          <div className="cartx-layout">
            <div className="cartx-product-list">
              <div className="cartx-select-all-row">
                <label className="cartx-select-control">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={() => handleToggleSelectAll(activeProducts)}
                  />
                  <span>Chọn nhanh tối đa 4 sản phẩm</span>
                </label>
              </div>
              {activeProducts.map((activeProduct) => (
                <article key={`${activeProduct?.productId}_${activeProduct?.packageId || "default"}`} className="cartx-product-card">
                  <div className="cartx-card-head">
                    <label className="cartx-select-control">
                      <input
                        type="checkbox"
                        checked={selectedItemKeys.includes(getCartItemKey(activeProduct))}
                        onChange={() => handleToggleSelectItem(activeProduct)}
                      />
                      <span>Chọn thanh toán</span>
                    </label>
                  </div>
                  <img className="cartx-product-image" src={activeProduct?.product_imgs?.[0]} alt={activeProduct?.product_name || "Product"} />
                  <div className="cartx-product-body">
                    <span className="cartx-product-chip">{activeProduct?.product_type || "product"}</span>
                    <h2 className="cartx-product-name">{activeProduct?.product_name}</h2>
                    {activeProduct?.selected_package?.name && (
                      <p className="cartx-qty">Gói: {activeProduct.selected_package.name}</p>
                    )}
                    <p className="cartx-product-price">{getProductPriceLabel(activeProduct)}</p>
                    <div className="cartx-product-meta">
                      <span className="cartx-qty">Số lượng: 1</span>
                      <span className="cartx-status">Trạng thái: {activeProduct?.product_status === "active" ? "Sẵn sàng" : "Tạm hết"}</span>
                    </div>
                    <button
                      type="button"
                      className="cartx-clear-btn"
                      onClick={() => handleRemoveItem(activeProduct.productId, activeProduct.packageId)}
                    >
                      <MdDelete />
                      <span>Xóa sản phẩm</span>
                    </button>
                  </div>
                </article>
              ))}
            </div>
            
            <aside className="cartx-summary-card">
              <h3 className="cartx-summary-title">Tóm tắt đơn hàng</h3>
              <div className="cartx-summary-row">
                <span>Số sản phẩm</span>
                <span>{activeProducts.length}/4</span>
              </div>
              <div className="cartx-summary-row">
                <span>Đã chọn</span>
                <span>{selectedProducts.length}</span>
              </div>
              <div className="cartx-summary-row">
                <span>Tạm tính</span>
                <span>{subtotal.toLocaleString("vi-VN")}đ</span>
              </div>
              <div className="cartx-summary-row">
                <span>Giảm giá</span>
                <span>0đ</span>
              </div>
              <div className="cartx-summary-total">
                <span>Tổng cộng</span>
                <span>{subtotal.toLocaleString("vi-VN")}đ</span>
              </div>
              <div className="cartx-summary-actions">
                <button
                  onClick={() => {
                    if (selectedProducts.length === 0) {
                      showToast("Vui lòng chọn ít nhất 1 sản phẩm để thanh toán", "warning");
                      return;
                    }

                    navigate(`/checkout`, {
                      state: {
                        checkout_items: selectedProducts
                      }
                    });
                  }}
                  className="cartx-checkout-btn"
                  type="button"
                  disabled={selectedProducts.length === 0}
                >
                  Thanh toán ngay
                  <FaArrowRightLong />
                </button>
              </div>
            </aside>
          </div>
        ) : (
          <div className="cartx-empty">
            <img className="cartx-empty-image" src={EMPTY_CART} alt="Empty Cart" />
            <p className="cartx-empty-title">Giỏ hàng đang trống</p>
            <p className="cartx-empty-subtitle">Bạn chưa thêm sản phẩm nào. Quay lại danh mục để chọn sản phẩm phù hợp nhé.</p>
            <button className="cartx-empty-btn" type="button" onClick={() => navigate("/category/game-account")}>
              Khám phá sản phẩm
            </button>
          </div>
        )}
      </div>
    </section>
  );
};
